/* eslint-disable no-shadow */
/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useState, useEffect } from 'react';

import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useQueryClient, useQuery, useMutation } from '@tanstack/react-query';

import Progress from '../../components/LenderOnboarding/Progress';
import FormContainer from '../../components/AddOrEditPage/FormContainer';
import SettingsContainer from '../../components/AddOrEditPage/SettingsContainer';
import TermsOfService from '../../components/LenderOnboarding/TermsOfService';

import useLenderOnboardingStepOne from '../../forms/lender/onboarding/step1';
import useLenderOnboardingStepTwo from '../../forms/lender/onboarding/step2';
import useLenderOnboardingStepThree from '../../forms/lender/onboarding/step3';
import logoBlack from '../../assets/images/logo-black.png';
import onboardingDecoration from '../../assets/images/auth-decoration.png';
import AuthService from '../../services/AuthService';
import LenderService from '../../services/LenderService';
import { useUiContext } from '../../context/UiContext';
import useLenderData from '../../hooks/useLenderData';
import useSignOut from '../../hooks/useSignOut';

const areArraysEqualById = (arr1, arr2) => {
  if (arr1.length !== arr2.length) {
    return false;
  }
  return arr1?.every?.((item) =>
    arr2.some((otherItem) => otherItem.id === item.id)
  );
};

export default function LenderOnboardingPage() {
  const [step, setStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isAccepted, setIsAccepted] = useState(false);
  const [unOnboardedLenders, setUnOnboardedLenders] = useState([]);
  const [hasTriedToSubmit, setHasTriedToSubmit] = useState(false);

  const { showToast } = useUiContext();

  const queryClient = useQueryClient();

  const navigate = useNavigate();
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const isTos = query.get('terms');

  const { data: me } = useQuery({
    queryKey: ['me'],
    queryFn: AuthService.getMe,
  });

  const { lenders, refetch } = useLenderData(me);

  useEffect(() => {
    if (lenders?.length) {
      const notOnboardedLenders = lenders?.filter(
        (lender) => !lender?.onboarded
      );

      if (!areArraysEqualById(notOnboardedLenders, unOnboardedLenders)) {
        setUnOnboardedLenders(notOnboardedLenders);
      }
    }
  }, [lenders, unOnboardedLenders]);

  const stepOneForm = useLenderOnboardingStepOne(
    () => setStep(1),
    unOnboardedLenders[0] || {},
    'some'
  );
  const stepTwoForm = useLenderOnboardingStepTwo(
    () => setStep(2),
    unOnboardedLenders[0] || {}
  );
  const stepThreeForm = useLenderOnboardingStepThree(
    () => setStep(3),
    unOnboardedLenders[0] || {}
  );

  useEffect(() => {
    if (isTos) {
      setStep(3);
    }
  }, [isTos]);

  const signOut = useSignOut();

  const { mutate: submitOnboarding } = useMutation({
    mutationFn: () => {
      setIsSubmitting(true);

      if (isTos) {
        return Promise.all(
          lenders.map((lender) => {
            return LenderService.updateLender({
              formValues: {
                accepted_tos_date: new Date(),
              },
              lenderId: lender?.id,
            });
          })
        );
      }

      return LenderService.updateLender({
        formValues: {
          ...stepOneForm.formik.values,
          ...stepTwoForm.formik.values,
          ...stepThreeForm.formik.values,
          onboarded: true,
          accepted_tos_date: new Date(),
        },
        lenderId: unOnboardedLenders[0]?.id,
      });
    },
    onSuccess: async (lender) => {
      if (isTos) {
        lender.forEach((lender) => {
          queryClient.setQueryData(['lenderFromHook', lender.id], lender);
        });
      } else {
        queryClient.setQueryData(['lenderFromHook', lender.id], lender);
      }

      refetch();
      if (unOnboardedLenders.length > 1) {
        setUnOnboardedLenders((prevState) => prevState.slice(1));
        showToast({
          text: 'You have multiple lender accounts. Please complete onboarding for all of them.',
          type: 'success',
        });
        setStep(0);
        setIsAccepted(false);
        setHasTriedToSubmit(false);
      } else {
        if (!isTos) {
          showToast({
            text: 'Onboarding completed successfully',
            type: 'success',
          });
        }

        navigate('/loans');
      }
    },
    onError: (error) => {
      console.log(error);
      showToast({
        text: error?.response?.data?.message || 'Something went wrong',
        type: 'error',
      });
    },
    onSettled: () => {
      setIsSubmitting(false);
    },
  });

  const switchSteps = () => {
    switch (step) {
      case 0:
        stepOneForm.formik.submitForm();
        break;
      case 1:
        stepTwoForm.formik.submitForm();
        break;
      case 2:
        stepThreeForm.formik.submitForm();
        break;
      case 3:
        if (!isAccepted) {
          setHasTriedToSubmit(true);
        } else {
          submitOnboarding();
        }
        break;
      default:
        break;
    }
  };

  const goToStep = (stepNumber) => {
    if (isTos) {
      return;
    }

    switch (stepNumber) {
      case 0:
        setStep(0);
        break;
      case 1:
        if (stepOneForm.formik.isValid) {
          setStep(1);
        }
        break;
      case 2:
        if (stepTwoForm.formik.isValid && stepOneForm.formik.isValid) {
          setStep(2);
        }
        break;
      case 3:
        if (
          stepThreeForm.formik.isValid &&
          stepTwoForm.formik.isValid &&
          stepOneForm.formik.isValid
        ) {
          setStep(3);
        }
        break;
      default:
        break;
    }
  };

  return (
    <main className="bg-white dark:bg-slate-900">
      <div className="relative flex">
        {/* Content */}
        <div className="w-full md:w-1/2">
          <div className="min-h-[100dvh] h-full flex flex-col after:flex-1">
            <div className="flex-1">
              {/* Header */}
              <div className="flex items-center justify-between h-16 px-4 sm:px-6 lg:px-8">
                {/* Logo */}
                <Link className="block" to="/">
                  <img src={logoBlack} width="32" height="32" alt="Logo" />
                </Link>
              </div>

              {/* Progress bar */}
              {!isTos && (
                <Progress stepsNumber={4} step={step} goToStep={goToStep} />
              )}
            </div>

            <div className="px-4 py-8">
              <div className="max-w-md mx-auto">
                <h1 className="text-3xl text-slate-800 dark:text-slate-100 font-bold mb-6">
                  Onboarding ✨
                </h1>
                {/* Form */}
                <div>
                  <div className="space-y-3 mb-8">
                    {step === 0 && (
                      <FormContainer
                        form={stepOneForm}
                        isVisible
                        noSubmit
                        gridStyle="grid gap-5 md:grid-cols-1 xl:grid-cols-2"
                        noBillComId
                      />
                    )}
                    {step === 1 && (
                      <FormContainer
                        form={stepTwoForm}
                        isVisible
                        noSubmit
                        gridStyle="grid gap-5 md:grid-cols-1 xl:grid-cols-2"
                        noBillComId
                      />
                    )}
                    {step === 2 && (
                      <SettingsContainer
                        form={stepThreeForm}
                        isVisible
                        noSubmit
                        noBorder
                      />
                    )}
                    {step === 3 && (
                      <TermsOfService
                        value={isAccepted}
                        setValue={setIsAccepted}
                        name={unOnboardedLenders[0]?.name}
                        hasTriedToSubmit={hasTriedToSubmit}
                      />
                    )}
                  </div>
                  <div className="flex items-center justify-between">
                    <button
                      disabled={isSubmitting}
                      className="btn bg-indigo-500 hover:bg-indigo-600 text-white ml-auto"
                      type="button"
                      onClick={switchSteps}
                    >
                      {step < 3 ? <>Next Step -&gt;</> : 'Submit'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <button
              onClick={signOut}
              type="button"
              className="absolute bottom-4 left-8 text-sm text-indigo-500 dark:text-indigo-400 hover:text-indigo-600 dark:hover:text-indigo-500"
            >
              Sign out
            </button>
          </div>
        </div>

        {/* Image */}
        <div
          className="hidden md:block absolute top-0 bottom-0 right-0 md:w-1/2"
          aria-hidden="true"
        >
          <img
            className="object-cover object-center w-full h-full"
            src={`https://images.unsplash.com/photo-1572120360610-d971b9d7767c?q=80&h=${window.innerHeight}&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D`}
            width="760"
            height="1024"
            alt="Onboarding"
          />
          <img
            className="absolute top-1/4 left-0 -translate-x-1/2 ml-8 hidden lg:block"
            src={onboardingDecoration}
            width="218"
            height="224"
            alt="Authentication decoration"
          />
        </div>
      </div>
    </main>
  );
}
